import type { Item, Level } from '../../core/hp/types';
import { translate } from '../../i18n/I18n';
import type { Store } from '../../state/store';
import type { RtClock } from './connection';

/** As long as the model library's own request deadline: a tap that waits longer is told, and a retry finds what has come since. */
export const PLACEMENT_DEADLINE_MS = 20_000;

/** True for a floor or ceiling piece on a floor with no room: the app's add names the room's floor or ceiling it stands in. */
export function noRoomFor(level: Pick<Level, 'rooms'> | null | undefined, placement: Item['placement']): boolean {
  return placement !== 'wall' && !level?.rooms.some(room => room.polygon.length >= 3);
}

/**
 * What a catalog tap in the room must still find when its model is ready: the same open document, the same floor by its
 * id, item editing, and with `generation` (`LiveSession.generation`) the same copy of the room, never one after a rejoin.
 */
export function placementScope(store: Store, generation?: number): string | null {
  const scope = store.captureDocumentScope();
  if (!scope || store.get().edit !== 'items') return null;
  return `${scope.projectId}:${scope.epoch}:${store.level?.uniqueId ?? `#${scope.levelIndex}`}${generation === undefined ? '' : `:${generation}`}`;
}

export interface LivePlacementDeps<P> {
  /** True once the pick can be encoded as the app will read it: its card's definition read, its model measured; `signal` aborts when the tap stops waiting. */
  ready: (pick: P, signal: AbortSignal) => Promise<boolean>;
  /** Why the pick can never go in, whatever the wait brings, or null. */
  refusal: (pick: P) => string | null;
  /** The pick as its card stands now, its PRO mark included; null when the card is no longer listed, undefined when no list can tell (hidden, loading): the tap's own pick goes. */
  refresh: (pick: P) => P | null | undefined;
  /** A model ready while the store holds a stroke open waits for the stroke to end: placed inside it, the add would join the drag. */
  held?: Pick<Store, 'get' | 'subscribe' | 'documentBusy' | 'documentSettled'>;
  scope: () => string | null;
  place: (pick: P) => void;
  /** Nothing was sent and the person is told: `reason` is a refusal said at the tap or the card gone from the list, none when the model or the card's definition did not come. */
  fail: (reason?: string) => void;
  onPending: (pick: P | null) => void;
  clock: Pick<RtClock, 'setTimeout' | 'clearTimeout'>;
}

/**
 * A catalog tap in the app's live room: the piece waits for what its add must carry, one tap at a time — a newer tap
 * replaces the one still waiting — and is dropped when the room moves on meanwhile or the deadline passes. A pick the
 * room can never take is refused at the tap, with no wait.
 */
export class LivePlacement<P> {
  private token = 0;
  private waiting: { token: number; timer: unknown; stop: AbortController; unsubscribe?: () => void } | null = null;

  constructor(private readonly deps: LivePlacementDeps<P>) {}

  start(pick: P): void {
    this.cancel();
    const scope = this.deps.scope();
    if (scope === null) return;
    const refusal = this.deps.refusal(pick);
    if (refusal !== null) { this.deps.fail(refusal); return; }
    const token = ++this.token;
    const timer = this.deps.clock.setTimeout(() => { if (this.end(token) && this.deps.scope() === scope) this.deps.fail(); }, PLACEMENT_DEADLINE_MS);
    const stop = new AbortController();
    this.waiting = { token, timer, stop };
    this.deps.onPending(pick);
    void Promise.resolve().then(() => this.deps.ready(pick, stop.signal)).catch(() => false).then(ok => {
      if (ok && this.waiting?.token === token && this.deps.scope() === scope && this.deps.held?.get().isDragging) { this.holdOn(token, scope, pick); return; }
      if (!this.end(token) || this.deps.scope() !== scope) return;
      if (!ok) { this.deps.fail(); return; }
      this.place(pick);
    });
  }

  cancel(): void {
    if (this.waiting) this.end(this.waiting.token);
  }

  private place(pick: P): void {
    const fresh = this.deps.refresh(pick);
    if (fresh === null) this.deps.fail(translate('#WebLiveCardGone'));
    else this.deps.place(fresh ?? pick);
  }

  /** The model is in: no deadline is left to run, only the stroke and the edit it commits to see out. */
  private holdOn(token: number, scope: string, pick: P): void {
    const held = this.deps.held!, waiting = this.waiting!;
    this.deps.clock.clearTimeout(waiting.timer);
    let queued = false;
    waiting.unsubscribe = held.subscribe(() => {
      if (queued || held.get().isDragging) return;
      queued = true;
      void held.documentSettled().then(() => {
        queued = false;
        if (this.waiting?.token !== token) return;
        if (this.deps.scope() !== scope) { this.end(token); return; }
        if (!held.get().isDragging && !held.documentBusy && this.end(token)) this.place(pick);
      });
    });
  }

  private end(token: number): boolean {
    if (this.waiting?.token !== token) return false;
    this.deps.clock.clearTimeout(this.waiting.timer);
    this.waiting.stop.abort();
    this.waiting.unsubscribe?.();
    this.waiting = null;
    this.deps.onPending(null);
    return true;
  }
}
